/**
 * src/lib/verification/verificationRequirements.ts
 *
 * VERIFICATION REQUIREMENTS — the single source of truth for "what does this
 * owner actually need?" per vertical.
 *
 * ## Why this file exists
 *
 * The Verification Centre grew organically for about two years and had drifted
 * into three different jobs at once:
 *
 *   1. identity/KYC (who is this person?),
 *   2. compliance documents the law places on the owner, and
 *   3. marketing badges that make a profile look trustworthy.
 *
 * Worse, the UI *claimed* requirements that nothing in the codebase enforced —
 * the Stay "core trust badges" card counted Fire Safety and Health & Safety as
 * mandatory when `stayPaymentGate.ts` only ever gated HURU + ID (+ CIPC when a
 * business signal was guessed). HURU itself was not an independent document at
 * all: submitting an ID mirrored its result into `huru_clearance_status`, so
 * "HURU verified" was really just "ID verified" wearing a second name.
 *
 * This module fixes that by separating the three concerns explicitly:
 *
 *   - **Layer 1 — Verified.** Identity plus one proof that the business exists.
 *     Earned status only. It gates nothing; it earns ranking weight and a badge.
 *   - **Layer 2 — Trust signals.** Optional documents that earn extra badges.
 *   - **Layer 3 — Inform only.** Legal duties that belong to the owner (SARS
 *     income tax, POPIA, municipal zoning). We never verify these, never gate on
 *     them, and never display them as though we had confirmed them. Encoding them
 *     as requirements would create false assurance and expose Zamzi to liability.
 *
 * ## The sole-prop / registered question
 *
 * Every owner is asked once: are you a **sole proprietor** or a **registered
 * business**? The answer decides which Layer-1 documents are required:
 *
 *   - Sole prop  → the owner *is* the business. ID is the business proof.
 *   - Registered → the entity must exist on paper. CIPC + SARS tax compliance.
 *
 * This replaced a guess. `stayPaymentGate.hasBusinessSignal()` inferred
 * "registered business" from whether the owner happened to fill in
 * `companyLegalName` / `cipcRegistrationNumber`, so a sole-prop host who left
 * the company field blank was silently blocked from taking payment, and a
 * registered host who forgot to type their company name was blocked too. Nobody
 * was ever asked. `resolveBusinessEntity` now reads an explicit declaration.
 *
 * ## Layer 1 varies by vertical on purpose
 *
 * Identity alone is not enough everywhere:
 *
 *   - **Stay** additionally requires proof the host controls the property, and
 *     banks payouts to that same person. A guest handing money to an anonymous
 *     stranger is the exact fraud Airbnb's model is built to prevent.
 *   - **Restaurant** additionally requires the municipal Certificate of
 *     Acceptability (Reg. R638). Serving food without one is a criminal offence
 *     in South Africa, not a nice-to-have.
 *   - **Fix-it** additionally requires the certificate for the trade the owner
 *     declared. Bad work is a life-safety risk, so a claim without the matching
 *     trade certificate is worthless as a trust signal.
 *   - **Retail** additionally requires CIPC *or* sole-prop proof — the store
 *     must demonstrably exist.
 *
 * Layer 1 is deliberately identical across all four verticals at its core
 * (ID + declared business proof) so that a new vertical cannot invent its own
 * identity policy. Only the vertical-specific second document differs.
 */

import {
  isVerificationStatusPending,
  isVerificationStatusVerified,
} from "./verificationStatus";
import type { DocumentType } from "@/types/verification";
import {
  CERTIFICATION_GUIDANCE_BY_DOCUMENT,
  CERTIFICATION_LABEL_BY_DOCUMENT,
  resolveRequiredCertifications,
} from "@/lib/fixIt/tradeCertification";
import { getVerificationStatusFieldForDocumentType } from "@/lib/verification/documentTypeRegistry";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type VerificationVertical = "stay" | "restaurant" | "retail" | "fixit";

/**
 * Which Layer-1 business document an owner must supply.
 *
 * Deliberately absent: HURU. It was never an independent document — an ID
 * submission mirrored into `huru_clearance_status` — and it is being retired
 * from the owner's experience. See RETIREMENT_NOTES below.
 */
export type BusinessEntity = "sole_prop" | "registered" | "unknown";

export type RequirementGroup = "verified" | "trust" | "inform";

/** A single thing an owner can be asked for. */
export interface VerificationRequirement {
  /** Stable id, e.g. 'id_document', 'stay_property_control'. */
  key: string;
  /** Owner-facing label. */
  label: string;
  /** One short sentence explaining *why* it matters to a customer. */
  why: string;
  group: RequirementGroup;
  /**
   * Profile field holding the verification status, when the requirement is
   * satisfied by an AI-vetted document. Absent for requirements satisfied by a
   * stored upload (e.g. Stay property-control evidence).
   */
  statusField?: string;
  /**
   * The vetted document type that writes .
   *
   * Present whenever the requirement is satisfied by an AI-vetted document, and
   * it is what the Verification Centre renders its upload field from. Keeping
   * this next to the requirement is the point: the manifest drives the
   * checklist, the upload button and the badge tier from one definition, so an
   * owner can no longer be shown a requirement with no way to complete it.
   */
  documentType?: DocumentType;
  /** True when an uploaded URL on the profile satisfies it. */
  uploadField?: string;
  /** For trade certificates: only required for the trade the owner declared. */
  appliesToTrade?: boolean;

  /**
   * Profile boolean that records a declaration the owner gave, when the
   * requirement is satisfied by agreeing to wording rather than by uploading.
   *
   * Used by the Stay property-rights declaration and the retail electrical
   * compliance warranty. Neither is a document, and pretending otherwise is
   * what made them invisible here.
   */
  declarationField?: string;
  /**
   * Profile field holding the wording version the owner agreed to.
   *
   * A declaration without a version cannot be shown again when the words
   * change, which is the only thing that makes a stored assent meaningful.
   */
  declarationVersionField?: string;

  /**
   * When this requirement applies at all, declared rather than computed.
   *
   * `field === true` reads as "only required when the owner has answered yes to
   * this field". Kept as data so the manifest stays serialisable - it is
   * imported by client components - rather than carrying a predicate that would
   * have to cross a server/client boundary as an opaque function.
   */
  condition?: { field: string; equals: boolean };
}

export interface InformationalNotice {
  key: string;
  label: string;
  /**
   * Shown as guidance only. Must never read as "Zamzi has checked this" — these
   * are the owner's own legal obligations and we have no way to verify them.
   */
  body: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared building blocks
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The retail electrical compliance warranty.
 *
 * Conditional on the seller having said they carry out electrical work. Most
 * Shopaholic sellers sell clothes, food or crafts and never touch wiring, so
 * asking all of them is bureaucracy for its own sake and buries the ones who do.
 * The minority who answer yes are exactly the people it protects.
 *
 * A warranty, not a check. Nothing in the UI may imply we verified it.
 */
const ELECTRICAL_WORKS_WARRANTY: VerificationRequirement = {
  key: "electrical_works_warranty",
  label: "Electrical compliance warranty",
  why: "Work on an installation must be certified with an ECoC under SANS 10142, and the certificate belongs to the customer. You warrant both.",
  group: "verified",
  declarationField: "retailEctWarranty",
  declarationVersionField: "retailEctWarrantyVersion",
  condition: { field: "retailDoesElectricalWork", equals: true },
};

const ID_DOCUMENT: VerificationRequirement = {
  key: "id_document",
  label: "ID verification",
  why: "Proves you are a real person. It unlocks your Verified badge everywhere on Zamzi.",
  group: "verified",
  statusField: "id_document_status",
  documentType: "ID_DOCUMENT",
};

const CIPC_REGISTRATION: VerificationRequirement = {
  key: "cipc_registration",
  label: "CIPC registration",
  why: "Shows your company is legally registered and active.",
  group: "verified",
  statusField: "cipc_registration_status",
  documentType: "CIPC_REGISTRATION",
};

const SARS_TAX_COMPLIANCE: VerificationRequirement = {
  key: "sars_tax_compliance",
  label: "SARS tax compliance",
  why: "Shows your business is tax compliant.",
  group: "verified",
  statusField: "sars_clearance_status",
  documentType: "SARS_TAX_COMPLIANCE",
};

const SOLE_PROP_PROOF: VerificationRequirement = {
  key: "sole_prop_business_proof",
  label: "Business proof",
  why: "Sole proprietors do not have CIPC papers, so we accept bank or business-account proof that the business is really yours.",
  group: "verified",
  statusField: "sole_prop_business_proof_status",
  documentType: "SOLE_PROP_BUSINESS_PROOF",
};

// ─────────────────────────────────────────────────────────────────────────────
// Layer 2 — bundled trust signals
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Optional documents that earn a *client-facing* badge rather than another
 * individual icon. Four bundles, chosen so a single upload is visible reward:
 *
 *   Safe & Clean Kitchen ← food handler + cleaning chemicals + pest control
 *   Licensed Bar         ← liquor licence
 *   Fire Safe            ← EMS fire safety certificate
 *   Certified Kitchen    ← ISO 22000 / FSSC 22000 (food-safety *systems*)
 *   Star Graded          ← TGCSA (tourism grading)
 *
 * `Certified Kitchen` and `Star Graded` were deliberately split. An earlier
 * draft bundled ISO + FSSC + TGCSA together, which meant a restaurant running
 * an ISO system and a guest house holding TGCSA stars would display an
 * identical badge — true, but meaningless to the customer reading it.
 */
/**
 * The Certificate of Acceptability, mandatory for anyone preparing food for sale.
 *
 * A CoA is issued under Regulation R638 for ONE specific premises. Serving food
 * without one is a criminal offence in South Africa, not a nice-to-have - and a
 * CoA belonging to a different kitchen is the same as having none, which is why
 * the premises cross-check exists.
 *
 * Previously listed as an optional trust signal. That was wrong twice over: it
 * under-stated a legal duty, and it let a vendor trade with no CoA at all while
 * their profile carried the same badge as one that had.
 */
const CERTIFICATE_OF_ACCEPTABILITY: VerificationRequirement = {
  key: "certificate_of_acceptability",
  label: "Certificate of Acceptability",
  why: "The law requires one to prepare food for sale. Diners can see your kitchen passed a municipal health inspection.",
  group: "verified",
  statusField: "coa_certificate_status",
  documentType: "COA_CERTIFICATE",
};

const RESTAURANT_TRUST: VerificationRequirement[] = [
  {
    key: "liquor_license",
    label: "Liquor licence",
    why: "Confirms you are licensed to serve alcohol.",
    group: "trust",
    statusField: "liquor_license_status",
    documentType: "LIQUOR_LICENSE",
  },
  {
    key: "fire_safety",
    label: "Fire safety certificate",
    why: "Shows your premises passed an emergency services fire inspection.",
    group: "trust",
    statusField: "ems_fire_safety_certificate_status",
    documentType: "EMS_FIRE_SAFETY_CERTIFICATE",
  },
  {
    key: "food_handler",
    label: "Food handler training",
    why: "Shows your staff are trained to handle food safely.",
    group: "trust",
    statusField: "food_handler_certificate_status",
    documentType: "FOOD_HANDLER_CERTIFICATE",
  },
  {
    key: "cleaning_chemicals",
    label: "Cleaning chemicals approval",
    why: "Confirms the chemicals used in your kitchen are food-safe.",
    group: "trust",
    statusField: "cleaning_chemicals_approval_status",
    documentType: "CLEANING_CHEMICALS_APPROVAL",
  },
  {
    key: "pest_control",
    label: "Pest control certificate",
    why: "Shows a registered provider manages pest control at your premises.",
    group: "trust",
    statusField: "pest_control_certificate_status",
    documentType: "PEST_CONTROL_CERTIFICATE",
  },
  {
    key: "iso_22000",
    label: "ISO 22000 food safety management",
    why: "Shows you run a certified food safety management system.",
    group: "trust",
    statusField: "iso_22000_certificate_status",
    documentType: "ISO_22000_CERTIFICATE",
  },
  {
    key: "fssc_22000",
    label: "FSSC 22000 food safety certification",
    why: "Shows your food safety system is independently certified.",
    group: "trust",
    statusField: "fssc_22000_certificate_status",
    documentType: "FSSC_22000_CERTIFICATE",
  },
];

const STAY_TRUST: VerificationRequirement[] = [
  {
    key: "certificate_of_acceptability",
    label: "Certificate of Acceptability",
    why: "Required by municipal law if you serve breakfast or any meals.",
    group: "trust",
    statusField: "coa_certificate_status",
    documentType: "COA_CERTIFICATE",
  },
  {
    key: "gas_coc",
    label: "Gas certificate of conformity",
    why: "Required if guests can use a stove, geyser or braai. They will ask for it.",
    group: "trust",
    statusField: "gas_coc_status",
    documentType: "GAS_COC",
  },
  {
    key: "fire_safety",
    label: "Fire safety certificate",
    why: "Shows your property passed an emergency services inspection.",
    group: "trust",
    statusField: "ems_fire_safety_certificate_status",
    documentType: "EMS_FIRE_SAFETY_CERTIFICATE",
  },
  {
    key: "food_handler",
    label: "Food handler training",
    why: "Only relevant if you serve meals, not for self-catering units.",
    group: "trust",
    statusField: "food_handler_certificate_status",
    documentType: "FOOD_HANDLER_CERTIFICATE",
  },
  {
    key: "tgcsa",
    label: "TGCSA star grading",
    why: "Corporate and government bookings often require official star grading.",
    group: "trust",
    statusField: "tgcsa_certificate_status",
    documentType: "TGCSA_CERTIFICATE",
  },
];

const RETAIL_TRUST: VerificationRequirement[] = [
  {
    key: "fire_safety",
    label: "Fire safety certificate",
    why: "Shows your store is safe for public foot traffic.",
    group: "trust",
    statusField: "ems_fire_safety_certificate_status",
    documentType: "EMS_FIRE_SAFETY_CERTIFICATE",
  },
];

const FIXIT_TRUST: VerificationRequirement[] = [
  {
    key: "plumbing_coc",
    label: "Plumbing COC (PIRB)",
    why: "Proof you may issue a plumbing certificate of compliance.",
    group: "trust",
    statusField: "plumbing_coc_status",
    documentType: "PLUMBING_COC",
    appliesToTrade: true,
  },
  {
    key: "electrical_coc",
    label: "Electrical COC",
    why: "Legally required before you may issue an electrical compliance certificate.",
    group: "trust",
    statusField: "electrical_coc_status",
    documentType: "ELECTRICAL_COC",
    appliesToTrade: true,
  },
  {
    key: "gas_coc",
    label: "Gas COC",
    why: "Required for gas appliance installation and maintenance.",
    group: "trust",
    statusField: "gas_coc_status",
    documentType: "GAS_COC",
    appliesToTrade: true,
  },
  {
    key: "electric_fence_coc",
    label: "Electric fence COC",
    why: "Required for altered or post-2012 electric fence installations.",
    group: "trust",
    statusField: "electric_fence_coc_status",
    documentType: "ELECTRIC_FENCE_COC",
    appliesToTrade: true,
  },
  {
    key: "pirb_registration",
    label: "PIRB registration",
    why: "Proves you are a registered plumbing practitioner.",
    group: "trust",
    statusField: "pirb_registration_status",
    documentType: "PIRB_REGISTRATION",
    appliesToTrade: true,
  },
  {
    key: "nhbrc_certificate",
    label: "NHBRC certificate",
    why: "Legally required for residential structural and renovation work.",
    group: "trust",
    statusField: "nhbrc_certificate_status",
    documentType: "NHBRC_CERTIFICATE",
    appliesToTrade: true,
  },
  {
    key: "saqcc_gas_practitioner",
    label: "SAQCC gas practitioner",
    why: "Required to certify gas work.",
    group: "trust",
    statusField: "saqcc_gas_practitioner_status",
    documentType: "SAQCC_GAS_PRACTITIONER",
    appliesToTrade: true,
  },
  {
    key: "water_installation",
    label: "Water installation certificate",
    why: "Required in some municipalities for property and plumbing work.",
    group: "trust",
    statusField: "water_installation_certificate_status",
    documentType: "WATER_INSTALLATION_CERTIFICATE",
    appliesToTrade: true,
  },
  {
    key: "repssa_registration",
    label: "REPSSA registration",
    why: "Required where your work includes security systems.",
    group: "trust",
    statusField: "repssa_registration_status",
    documentType: "REPSSA_REGISTRATION",
    appliesToTrade: true,
  },
  {
    key: "red_seal_certificate",
    label: "Red Seal certificate",
    why: "Nationally recognised artisan trade competency.",
    group: "trust",
    statusField: "red_seal_certificate_status",
    documentType: "RED_SEAL_CERTIFICATE",
  },
  {
    key: "public_liability_insurance",
    label: "Public liability insurance",
    why: "Shows cover if you damage a client property or someone is injured.",
    group: "trust",
    statusField: "public_liability_insurance_status",
    documentType: "PUBLIC_LIABILITY_INSURANCE",
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Layer 3 — inform only
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Shown as guidance, never as a requirement or a verification. These are the
 * owner's own legal obligations; we neither confirm nor gate them.
 */
const SHARED_INFORM: InformationalNotice[] = [
  {
    key: "sars_income_tax",
    label: "Declare your rental or trading income to SARS",
    body: "Every rand you earn through Zamzi is taxable income. This is your obligation to SARS, not something Zamzi can verify for you — keep your own records.",
  },
  {
    key: "popia",
    label: "POPIA privacy obligations",
    body: "If you collect guest or customer details directly — IDs at check-in, names, phone numbers — you are legally responsible for protecting that data.",
  },
  {
    key: "vat_threshold",
    label: "VAT registration thresholds",
    body: "Temporary VAT registration is generally only required once turnover passes the statutory threshold in a 12-month period. Check current SARS rules for your situation.",
  },
];

const STAY_INFORM: InformationalNotice[] = [
  {
    key: "estate_rules",
    label: "Check your estate or complex allows short-term lets",
    body: "If your Body Corporate or estate rules ban short-term holiday letting, you must not take direct bookings. A guest turned away at the gate is the fastest way to damage your rating and your business.",
  },
  {
    key: "municipal_zoning",
    label: "Municipal zoning and consent use",
    body: "Operating as a commercial guest house generally needs council consent, and rules differ per municipality. Cape Town is permissive for short residential lets; Johannesburg is not. Confirm with your municipality.",
  },
];

const RESTAURANT_INFORM: InformationalNotice[] = [
  {
    key: "trade_licence",
    label: "Municipal trade licence",
    body: "Food-handling businesses need a municipal trade or business licence in addition to the health certificate. Apply through your local council.",
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Vertical requirement sets
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Layer-1 requirements per vertical, before the owner's declared business type
 * is applied. The second document is the one that makes the vertical's trust
 * real to a customer.
 */
const VERTICAL_LAYER1: Record<VerificationVertical, VerificationRequirement[]> =
  {
    // A guest handing money to an anonymous stranger is precisely the fraud this
    // model exists to prevent, so Stay needs proof the host controls the property
    // and that payouts reach that same verified person.
    stay: [
      ID_DOCUMENT,
      /**
       * Proof the host controls THIS property.
       *
       * This carried `uploadField: 'stayPropertyControlProofUrl'`, which made it
       * satisfied by the mere presence of any stored file - any type, any content,
       * never vetted. The gate read `satisfied: has(profile, 'stayPropertyControlProofUrl')`,
       * so a host could pass the check that exists to stop a stranger taking money
       * for a property, by uploading a CIPC registration certificate. CIPC proves
       * a business exists; it says nothing about any property. That was found in
       * production when the owner did exactly this and the centre accepted it.
       *
       * It is now an AI-vetted document like every other Layer-1 requirement, and
       * `PROPERTY_CONTROL_PROOF`'s criteria fail CIPC, SARS, bank and licence
       * documents by name as the wrong type for this slot.
       *
       * `uploadField` is deliberately ABSENT. Keeping it would not merely weaken
       * this, it would defeat the change outright: `evaluateVerificationProgress`
       * falls back to `uploadField` whenever `statusField` is not verified
       * (line ~907), so any legacy upload would keep reading "done" forever and
       * the vetting would never gate anything. Do not add it back to preserve an
       * existing upload - that is the bug, not the compatibility.
       */
      {
        key: "stay_property_control",
        label: "Proof you may let this property",
        why: "A rates bill, lease, management agreement or title deed for THIS property. Your business registration is not accepted here - it proves your business exists, not that you control the property guests are booking.",
        group: "verified",
        statusField: "property_control_proof_status",
        documentType: "PROPERTY_CONTROL_PROOF",
      },
      {
        key: "stay_bank_confirmation",
        label: "Bank confirmation letter",
        why: "Lets us pay you. The account name must match your ID or your company documents.",
        group: "verified",
        uploadField: "stayBankConfirmationLetterUrl",
      },
    ],

    // Serving food without a Certificate of Acceptability is a criminal offence
    // in South Africa, so it is treated as part of proving the business is real.
    restaurant: [ID_DOCUMENT, CERTIFICATE_OF_ACCEPTABILITY],

    // Fix-it carries no static trade requirement. What is required depends
    // entirely on the trades the owner declared, and that is resolved per
    // profile by `getTradeCertificationRequirements` - see the note there on
    // why the old single "trade certificate" could never be completed.
    fixit: [ID_DOCUMENT],

    // Identity still applies - Retail's only own-document problem was the
    // business proof below, not identity.
    // Identity, the business proof resolved from the entity declaration, and -
    // only for sellers who carry out electrical work - the ECoC warranty.
    retail: [ID_DOCUMENT, ELECTRICAL_WORKS_WARRANTY],

    // Retail deliberately has no vertical document of its own. It previously
    // carried a "Proof your store is registered" field hardwired to
    // CIPC_REGISTRATION, which told a sole proprietor to upload company
    // registration papers they cannot legally obtain - and then rejected
    // whatever they sent instead. The shared `getBusinessProofRequirements`
    // already produces the right document for each answer: CIPC + SARS when
    // registered, bank proof when a sole prop. Retail uses that and nothing more.
  };

const VERTICAL_TRUST: Record<VerificationVertical, VerificationRequirement[]> =
  {
    stay: STAY_TRUST,
    restaurant: RESTAURANT_TRUST,
    retail: RETAIL_TRUST,
    fixit: FIXIT_TRUST,
  };

const VERTICAL_INFORM: Record<VerificationVertical, InformationalNotice[]> = {
  stay: STAY_INFORM,
  restaurant: RESTAURANT_INFORM,
  retail: [],
  fixit: [
    {
      key: "coida",
      label: "COIDA letter of good standing",
      body: "Companies with employees need an active letter of good standing from the Department of Labour to prove workers are insured against site injuries. A one-person show with no employees is exempt.",
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// Business-entity resolution
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Reads the owner's explicit sole-prop / registered declaration.
 *
 * Falls back to the legacy heuristic only when nothing was ever declared, so
 * that existing profiles keep working while new signups answer the question.
 * Once declared, the declaration always wins — that is the whole point.
 */
export function resolveBusinessEntity(
  profile: Partial<Record<string, unknown>> | null | undefined,
): BusinessEntity {
  const declared = profile?.businessEntity;
  if (declared === "sole_prop" || declared === "registered") {
    return declared;
  }

  const hasCipc = Boolean(
    asText(profile?.cipcRegistrationNumber) ||
    asText(profile?.founderCipcNumber) ||
    asText(profile?.cipc_registration_status),
  );
  if (hasCipc) return "registered";

  const hasCompanyName = Boolean(
    asText(profile?.companyLegalName) ||
    asText(profile?.companyRegistrationNumber),
  );
  if (hasCompanyName) return "registered";

  return "unknown";
}

/**
 * True when the owner has declared one way or the other. Owners who have not
 * answered must be asked before their requirements can be finalised — this is
 * the bug that silently blocked stay hosts who left the company field empty.
 */
export function hasDeclaredBusinessEntity(
  profile: Partial<Record<string, unknown>> | null | undefined,
): boolean {
  const declared = profile?.businessEntity;
  return declared === "sole_prop" || declared === "registered";
}

// ─────────────────────────────────────────────────────────────────────────────
// Requirement assembly
// ─────────────────────────────────────────────────────────────────────────────

/** The declared-entity document(s) that prove a business exists. */
export function getBusinessProofRequirements(
  entity: BusinessEntity,
): VerificationRequirement[] {
  if (entity === "registered") {
    // CIPC proves the company is registered; SARS TCS proves it is active.
    return [CIPC_REGISTRATION, SARS_TAX_COMPLIANCE];
  }
  if (entity === "sole_prop") {
    // The owner IS the business. A sole proprietor has no CIPC papers, so bank
    // or business-account proof is the equivalent document.
    return [SOLE_PROP_PROOF];
  }
  // Unknown: we cannot invent a requirement, but the owner still has to be told
  // what each answer would involve so the choice is informed.
  return [CIPC_REGISTRATION, SOLE_PROP_PROOF];
}

/**
 * The trade certification a Fix-it owner must supply, derived from the trades
 * they declared at signup.
 *
 * `trade_certificate` used to be a single static requirement with no document
 * type and no status field. It was listed as REQUIRED, appeared in the
 * checklist with a "Do this next" button, and had no upload control anywhere in
 * the codebase, so a Fix-it owner could never complete it. Worse, it asked a
 * plumber and a painter for the same undefined thing.
 *
 * It is now resolved from `fixitPrimaryTrade` / `fixitAdditionalTrades` through
 * the same compliance map the signup form has always used. A regulated trade
 * gets the specific document it actually needs; an unregulated one gets nothing,
 * because no statutory certificate exists for it.
 */
export function getTradeCertificationRequirements(
  profile: Partial<Record<string, unknown>> | null | undefined,
): VerificationRequirement[] {
  return resolveRequiredCertifications(profile).map((documentType) => ({
    key: `trade_${documentType.toLowerCase()}`,
    label: CERTIFICATION_LABEL_BY_DOCUMENT[documentType] ?? "Trade certification",
    why: CERTIFICATION_GUIDANCE_BY_DOCUMENT[documentType] ??
      "You told us your trade is regulated, so we need the registration that proves it.",
    group: "verified" as RequirementGroup,
    statusField:
      getVerificationStatusFieldForDocumentType(documentType) ?? undefined,
    documentType,
  }));
}

export interface VerticalVerificationSpec {
  vertical: VerificationVertical;
  /** Layer 1 — earns the Verified badge. Gates nothing. */
  required: VerificationRequirement[];
  /** Layer 2 — optional documents that earn extra badges. */
  trust: VerificationRequirement[];
  /** Layer 3 — guidance only, never verified, never gated. */
  inform: InformationalNotice[];
  /** True until the owner answers the sole-prop / registered question. */
  needsEntityDeclaration: boolean;
}

/**
 * Drop requirements that would render a second, redundant upload.
 *
 * Retail declares its own "proof your store is registered" and, for a registered
 * owner, the business proof also resolves to CIPC. Both write
 * , so the centre would render two identical upload
 * fields for the same document. The vertical's own requirement wins, because it
 * carries the better copy.
 *
 * Deduplicating here rather than curating each list by hand means this cannot
 * come back the next time a requirement is added.
 */
function dedupeRequirements(requirements: VerificationRequirement[]): VerificationRequirement[] {
  const seenDocuments = new Set<string>();
  const seenStatusFields = new Set<string>();
  const out: VerificationRequirement[] = [];

  for (const requirement of requirements) {
    const document = requirement.documentType ?? requirement.uploadField;
    if (document && seenDocuments.has(document)) continue;
    if (requirement.statusField && seenStatusFields.has(requirement.statusField)) continue;
    if (document) seenDocuments.add(document);
    if (requirement.statusField) seenStatusFields.add(requirement.statusField);
    out.push(requirement);
  }

  return out;
}

/**
 * The full requirement set for a vertical and a declared business entity.
 *
 * Identity comes first in Layer 1 on purpose: the order is the owner's order,
 * and "who are you" must be answered before "what is your business".
 */
/**
 * Whether a requirement applies to this profile at all.
 *
 * A conditional requirement is not "optional" - it is owed or not owed depending
 * on an answer the owner gave. Showing the electrical compliance warranty to a
 * seller who said they do no electrical work would be asking a settled question
 * forever, so the requirement simply is not in their list.
 *
 * Declarative rather than a predicate so the manifest stays serialisable; it is
 * imported by client components.
 */
export function requirementAppliesTo(
  requirement: VerificationRequirement,
  profile: Partial<Record<string, unknown>> | null | undefined,
): boolean {
  if (!requirement.condition) return true;
  return (profile?.[requirement.condition.field] ?? null) === requirement.condition.equals;
}

/** Drop conditional requirements this profile has not triggered. */
export function filterRequirementsForProfile(
  requirements: VerificationRequirement[],
  profile: Partial<Record<string, unknown>> | null | undefined,
): VerificationRequirement[] {
  return requirements.filter((requirement) => requirementAppliesTo(requirement, profile));
}

export function getVerificationSpec(
  vertical: VerificationVertical,
  entity: BusinessEntity = "unknown",
): VerticalVerificationSpec {
  const base = VERTICAL_LAYER1[vertical] ?? [];
  const businessProof = getBusinessProofRequirements(entity);

  // Identity first, then the vertical's own document, then the business proof.
  const ordered = [
    ...base.filter((r) => r.key === "id_document"),
    ...base.filter((r) => r.key !== "id_document"),
    ...businessProof,
  ];

  return {
    vertical,
    required: dedupeRequirements(ordered),
    trust: dedupeRequirements(VERTICAL_TRUST[vertical] ?? []),
    inform: [...SHARED_INFORM, ...(VERTICAL_INFORM[vertical] ?? [])],
    needsEntityDeclaration: entity === "unknown",
  };
}

/** Convenience: resolve the entity from the profile and build its spec. */
export function getVerificationSpecForProfile(
  vertical: VerificationVertical,
  profile: Partial<Record<string, unknown>> | null | undefined,
): VerticalVerificationSpec {
  const base = getVerificationSpec(vertical, resolveBusinessEntity(profile));

  /**
   * Conditional requirements are removed here rather than in the static lists.
   *
   * A seller who said they do no electrical work is not "optional" for the
   * warranty - they are not owed it. Leaving it in their list would put a settled
   * question in front of them on every visit to the Verification Centre.
   */
  const withoutInapplicable = filterRequirementsForProfile(base.required, profile);

  if (vertical !== "fixit") {
    return { ...base, required: withoutInapplicable };
  }

  // Trade certification can only be resolved from the owner, so it is injected
  // here rather than into the static vertical list.
  const certifications = getTradeCertificationRequirements(profile);
  return {
    ...base,
    required: dedupeRequirements([...withoutInapplicable, ...certifications]),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Progress evaluation
// ─────────────────────────────────────────────────────────────────────────────

export type RequirementState = "done" | "pending" | "todo";

export interface RequirementProgress {
  key: string;
  label: string;
  why: string;
  state: RequirementState;
}

export interface VerificationProgress {
  /** Layer-1 requirements, in owner-facing order. */
  items: RequirementProgress[];
  doneCount: number;
  totalCount: number;
  /** 0–100. 100 means every Layer-1 requirement is granted. */
  percent: number;
  isVerified: boolean;
  /** The single most useful next action, or null once fully verified. */
  nextAction: RequirementProgress | null;
}

const asText = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

/**
 * Identity, with the retired `huru_clearance_status` read as a legacy alias.
 *
 * Submitting an ID used to mirror its result into `huru_clearance_status`, so
 * hosts verified before `id_document_status` existed hold their proof only in
 * that field. Reading it here means those owners are never treated as
 * unidentified.
 */
export function isIdentityVerified(
  profile: Partial<Record<string, unknown>> | null | undefined,
): boolean {
  return (
    isVerificationStatusVerified(profile?.id_document_status) ||
    isVerificationStatusVerified(profile?.huru_clearance_status)
  );
}

/**
 * Evaluates Layer 1 for one profile.
 *
 * A requirement is satisfied either by a verified status field (AI-vetted or
 * admin-granted document) or by the presence of an uploaded file.
 */
export function evaluateVerificationProgress(
  spec: VerticalVerificationSpec,
  profile: Partial<Record<string, unknown>> | null | undefined,
): VerificationProgress {
  const items = spec.required.map<RequirementProgress>((requirement) => {
    let state: RequirementState = "todo";

    if (requirement.key === "id_document") {
      // Legacy alias: an ID submission used to mirror into huru_clearance_status,
      // so a host verified before that field existed still reads as identified.
      if (isIdentityVerified(profile)) {
        state = "done";
      } else if (
        isVerificationStatusPending(profile?.id_document_status) ||
        isVerificationStatusPending(profile?.huru_clearance_status)
      ) {
        state = "pending";
      }
    } else if (requirement.statusField) {
      const value = profile?.[requirement.statusField];
      if (isVerificationStatusVerified(value)) {
        state = "done";
      } else if (isVerificationStatusPending(value)) {
        state = "pending";
      }
    }

    if (state === "todo" && requirement.uploadField) {
      state = asText(profile?.[requirement.uploadField]) ? "done" : "todo";
    }

    /**
     * Declarations are satisfied by agreeing to wording, not by a status value.
     *
     * The version is checked as well as the flag. A stored assent to wording that
     * has since been changed is not an assent to what is on screen now, and
     * treating it as one would hold a host to a sentence they never read - the
     * same reason the declaration modules carry a version at all.
     */
    if (state === "todo" && requirement.declarationField) {
      const declared = profile?.[requirement.declarationField] === true;
      if (declared && requirement.declarationVersionField) {
        state = asText(profile?.[requirement.declarationVersionField]) ? "done" : "todo";
      } else if (declared) {
        state = "done";
      }
    }

    return {
      key: requirement.key,
      label: requirement.label,
      why: requirement.why,
      state,
    };
  });

  const doneCount = items.filter((item) => item.state === "done").length;
  const totalCount = items.length;

  // Next action skips in-flight items: an owner waiting on manual review has
  // nothing useful to do about *that* document, so we point them at the first
  // thing they can actually act on.
  const nextAction =
    items.find((item) => item.state !== "done" && item.state !== "pending") ??
    null;

  return {
    items,
    doneCount,
    totalCount,
    percent: totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0,
    isVerified: totalCount > 0 && doneCount === totalCount,
    nextAction,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Verification tiers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Public trust tier shown on the embeddable badge and the public profile.
 *
 * **Gold means the owner finished their whole Layer-1 path — whatever that path
 * is for their business type.** This is the whole point of the tier map:
 *
 *   Registered → ID + CIPC + SARS tax compliance + the vertical's own proof
 *                (Stay: property control + bank letter)
 *   Sole prop  → ID + the vertical's own proof + business proof
 *                (Stay: property control + bank letter, which *are* the
 *                 business proof — a sole prop has no CIPC papers to supply)
 *
 * A sole proprietor who completes their required path reaches Gold exactly like a
 * registered company does. Scoring them differently would penalise precisely the
 * group the verification model exists to protect.
 *
 * This was previously `id && (cipc || huru)`. Because an ID submission used to
 * mirror its result into `huru_clearance_status`, the `huru` term made `cipc`
 * redundant and Gold silently collapsed to "ID verified" — every sole-prop owner
 * was sitting at Gold without having proved anything about their business.
 */
export type VerificationTier = "gold" | "silver" | "standard";

/**
 * Evaluate a profile's tier.
 *
 * Delegates to the same Layer-1 spec the dashboard uses, so the badge can never
 * claim a tier the Verification Centre would disagree with.
 *
 * Owners who have not yet declared a business type are treated leniently. Their
 * spec lists BOTH possible business proofs, which would make Gold unreachable and
 * silently demote every existing verified owner to Silver on the day we ship
 * this. Instead they are graded on "identity + the vertical's own proof + at
 * least one business proof" — a floor, not a penalty. Declaring makes the
 * requirement exact.
 */
export function evaluateVerificationTier(
  vertical: VerificationVertical,
  profile: Partial<Record<string, unknown>> | null | undefined,
): VerificationTier {
  const entity = resolveBusinessEntity(profile);
  // The per-profile spec, not the raw vertical one. The raw spec still contains
  // conditional requirements this profile settled by answering NO — the retail
  // electrical-compliance warranty above all — and counting those as undone
  // locked every seller who answered NO out of gold forever (receipted live on
  // 2026-10-22: a fully gate-verified sole-prop seller holding a silver badge).
  const spec = getVerificationSpecForProfile(vertical, profile);
  const progress = evaluateVerificationProgress(spec, profile);

  const identity = progress.items.find((item) => item.key === "id_document");
  if (identity?.state !== "done") return "standard";

  if (entity === "unknown") {
    const businessProofKeys = new Set([
      "cipc_registration",
      "sole_prop_business_proof",
    ]);
    const base = progress.items.filter(
      (item) => !businessProofKeys.has(item.key),
    );
    const hasAnyBusinessProof = progress.items.some(
      (item) => businessProofKeys.has(item.key) && item.state === "done",
    );
    return base.every((item) => item.state === "done") && hasAnyBusinessProof
      ? "gold"
      : "silver";
  }

  return progress.isVerified ? "gold" : "silver";
}

// ─────────────────────────────────────────────────────────────────────────────
// Badge bundles (Layer 2 → client-facing trust)
// ─────────────────────────────────────────────────────────────────────────────

export interface TrustBadgeBundle {
  key: string;
  label: string;
  /** Status fields that each independently unlock this badge. */
  statusFields: string[];
  /** Shown on hover / in the badge sheet. */
  description: string;
}

/**
 * Client-facing badges so a restaurant owner who uploads five food-safety
 * certificates sees five fewer icons and one clear signal.
 *
 * Fix-it trade certificates are intentionally NOT bundled. A homeowner searching
 * for a plumber needs to see "Plumbing COC (PIRB)" specifically — bundling it
 * into a generic "certified professional" badge would destroy the exact signal
 * they are looking for. The UI caps how many badges it paints instead
 * (see DISPLAY_BADGE_CAP).
 */
export const TRUST_BADGE_BUNDLES: TrustBadgeBundle[] = [
  {
    key: "safe_clean_kitchen",
    label: "Safe & Clean Kitchen",
    statusFields: [
      "food_handler_certificate_status",
      "cleaning_chemicals_approval_status",
      "pest_control_certificate_status",
    ],
    description:
      "Food handler training, approved cleaning chemicals and pest control on file.",
  },
  {
    key: "licensed_bar",
    label: "Licensed Bar",
    statusFields: ["liquor_license_status"],
    description: "An active provincial liquor licence.",
  },
  {
    key: "fire_safe",
    label: "Fire Safe",
    statusFields: ["ems_fire_safety_certificate_status"],
    description: "Passed an emergency services fire inspection.",
  },
  {
    key: "certified_kitchen",
    label: "Certified Kitchen",
    statusFields: [
      "iso_22000_certificate_status",
      "fssc_22000_certificate_status",
    ],
    description: "An independently certified food safety management system.",
  },
  {
    key: "star_graded",
    label: "Star Graded",
    statusFields: ["tgcsa_certificate_status"],
    description: "Official TGCSA tourism grading.",
  },
];

/** Maximum badges painted before the UI shows "+N more". */
export const DISPLAY_BADGE_CAP = 4;

/**
 * Which trust badges a profile has earned.
 * Bundles are awarded when ANY member document is verified.
 */
export function getEarnedTrustBadges(
  profile: Partial<Record<string, unknown>> | null | undefined,
): TrustBadgeBundle[] {
  return TRUST_BADGE_BUNDLES.filter((bundle) =>
    bundle.statusFields.some((field) =>
      isVerificationStatusVerified(profile?.[field]),
    ),
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Retirement notes — kept deliberately, as documentation of intent
// ─────────────────────────────────────────────────────────────────────────────

export const RETIREMENT_NOTES = {
  /**
   * HURU is being removed from the owner's experience entirely: no badge, no
   * upload form, no copy, no gate.
   *
   * Under the hood `huru_clearance_status` is still READ as a legacy alias for
   * ID verification, because submitting an ID used to mirror its result into
   * that field. Many existing owners therefore hold "HURU verified" purely by
   * completing their ID — and that same value currently feeds search ranking and
   * the gold badge tier. Deleting it outright would silently strip ranking
   * weight and gold status from owners who have done nothing wrong.
   *
   * The alias is read-only. New ID submissions no longer write it. A future
   * migration can copy the values across and drop the field entirely; until
   * then, reading it costs nothing and protects real owners.
   */
  huru: "Retired from the owner experience; retained as a read-only legacy alias for ID so existing verified owners keep their ranking weight and gold tier.",

  /**
   * Fire Safety, Health & Safety, walkthrough video and geolocation photos were
   * displayed on the old Stay card as "core trust badges" that owners had to
   * earn. None of them ever gated anything. They are now Layer 2 trust
   * signals — still worth uploading, no longer dressed up as requirements.
   */
  fakeRequirements:
    "Documents that were displayed as mandatory but enforced nowhere are demoted to optional trust signals.",
} as const;
// ─────────────────────────────────────────────────────────────────────────────
// Public badge ordering
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Priority bands for the public badge strip. Lower number = painted first.
 *
 * The strip is ordered by what a customer is actually deciding with. A homeowner
 * scanning for a plumber is looking for "Plumbing", not "Red Seal", so trade
 * certificates sit above the generic tail. Bundles sit above individual trade
 * certificates because "Safe & Clean Kitchen" answers a question a customer
 * actually asks ("is this kitchen hygienic?") that "Food Handler Training" does
 * not.
 */
export const BADGE_PRIORITY = {
  identity: 1,
  business: 2,
  healthAndSafety: 3,
  bundle: 4,
  trade: 5,
  tax: 6,
  other: 7,
} as const;

/**
 * Rank a bundle against the individual badges.
 * Exposed so the strip and any future surface share one ordering.
 */
export function getTrustBadgePriority(key: string): number {
  return TRUST_BADGE_BUNDLES.some((bundle) => bundle.key === key)
    ? BADGE_PRIORITY.bundle
    : BADGE_PRIORITY.other;
}

/**
 * Cap a badge list for display, preserving order.
 *
 * A busy Fix-it contractor can legitimately hold nine trade certificates; eleven
 * pills across a search card reads as noise and drowns the profile name. The
 * remainder stays available behind a "+N more" toggle, so nothing is hidden,
 * only folded.
 */
export function capBadgesForDisplay<T>(
  badges: T[],
  cap: number = DISPLAY_BADGE_CAP,
): {
  visible: T[];
  hiddenCount: number;
} {
  if (cap <= 0) return { visible: [], hiddenCount: badges.length };
  return {
    visible: badges.slice(0, cap),
    hiddenCount: Math.max(0, badges.length - cap),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Upload routing
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Requirements whose upload UI lives somewhere other than the generic document
 * uploader, so the centre must not render a second copy of it.
 *
 * - `id_document` is a live camera + selfie flow (`IdVerificationCard`), not a
 *   file drop. Rendering the generic uploader for it too would ask the owner to
 *   verify their identity twice.
 * - `trade_certificate` is resolved from the owner's declared trade and has no
 *   single document type of its own.
 */
const SPECIAL_UPLOAD_KEYS = new Set(['id_document', 'trade_certificate']);

/**
 * Requirements the centre should render an AI-vetted upload field for.
 *
 * A requirement qualifies when it names a document type AND is not handled by a
 * bespoke flow. Anything in this list is guaranteed to have a working "submit"
 * button next to it, which is the invariant that used to be broken.
 */
export function getUploadableRequirements(requirements: VerificationRequirement[]): VerificationRequirement[] {
  return requirements.filter(
    (requirement) => Boolean(requirement.documentType) && !SPECIAL_UPLOAD_KEYS.has(requirement.key),
  );
}

/** Requirements satisfied by a stored evidence upload rather than a vetted document. */
export function getEvidenceUploadRequirements(requirements: VerificationRequirement[]): VerificationRequirement[] {
  return requirements.filter((requirement) => Boolean(requirement.uploadField));
}
